import { ApolloClient } from '@apollo/client';
import { Dispatch } from 'redux';

import UserApi from 'entities/user/api';
import { displayName } from 'utils/formatFullName';

import { CallActionPayload, MethodResponse, UIStateCallback } from '../../types';
import { ContextAPI } from '../ImpactAiContextProvider';

export const loadUser = async (
  payload: CallActionPayload,
  client: ApolloClient<unknown>,
  contextApi: ContextAPI,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>,
  uiStateCallback?: UIStateCallback
): Promise<MethodResponse> => {
  const { outputPath = 'user', waitForCompletion = true } = payload as {
    outputPath?: string;
    waitForCompletion?: boolean;
  };

  if (uiStateCallback) {
    uiStateCallback('processing');
  }

  const performLoad = async (): Promise<void> => {
    // Fetch user data
    const action = await UserApi.fetch().toPromise();

    // Dispatch to Redux store
    if (action && action.type) {
      dispatch(action);
    }

    // Transform user to context format
    const user = action?.user;
    if (!user) {
      throw new Error('No user data returned from API');
    }

    const userContext = {
      userName: displayName(user.toJS()),
      userEmail: user.email,
      userId: user.id,
      userType: user.type,
      organization: user.organization
        ? {
            id: user.organization.id,
            name: user.organization.name,
            type: user.organization.type,
          }
        : null,
    };

    // Set in context
    contextApi.setValue(outputPath, userContext);
  };

  if (!waitForCompletion) {
    performLoad()
      .then(() => {
        if (uiStateCallback) {
          uiStateCallback('complete');
        }
      })
      .catch((error) => {
        console.error('Background loadUser failed:', error);
      });

    if (uiStateCallback) {
      uiStateCallback('complete');
    }

    return {};
  }

  try {
    await performLoad();

    if (uiStateCallback) {
      uiStateCallback('complete');
    }

    return {};
  } catch (error) {
    const handleRetry = async () => {
      try {
        return await loadUser(payload, client, contextApi, dispatch, uiStateCallback);
      } catch (retryError) {
        if (uiStateCallback) {
          uiStateCallback('error', handleRetry);
        }
        return { error: true };
      }
    };

    if (uiStateCallback) {
      uiStateCallback('error', handleRetry);
    }
    return { error: true };
  }
};
